import axios from 'axios';
import {
  ContactList,
  CreateContactListData,
  UpdateContactListData
} from '../types/contact';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/contact-lists`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

class ContactListService {
  async getLists(): Promise<ContactList[]> {
    const response = await apiClient.get<ContactList[]>('');
    return response.data;
  }

  async getList(id: number, includeMembers: boolean = false): Promise<ContactList> {
    const response = await apiClient.get<ContactList>(`/${id}`, {
      params: { includeMembers }
    });
    return response.data;
  }

  async createList(listData: CreateContactListData): Promise<ContactList> {
    const response = await apiClient.post<ContactList>('', listData);
    return response.data;
  }

  async updateList(id: number, listData: UpdateContactListData): Promise<ContactList> {
    const response = await apiClient.put<ContactList>(`/${id}`, listData);
    return response.data;
  }

  async deleteList(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/${id}`);
    return response.data;
  }

  async addContactToList(listId: number, contactId: number): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(`/${listId}/members`, {
      contactId
    });
    return response.data;
  }

  async removeContactFromList(listId: number, contactId: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/${listId}/members/${contactId}`);
    return response.data;
  }

  async addMultipleContactsToList(listId: number, contactIds: number[]): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(`/${listId}/members/bulk`, {
      contactIds
    });
    return response.data;
  }
}

export const contactListService = new ContactListService();

